import { CloseIcon } from '@/components/icons/close-icon';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { getDirection } from '@/lib/constants';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { consumeSkipNextModalHistoryCleanup } from './history-cleanup';

const MODAL_HISTORY_STATE_KEY = '__modalOverlay';

// TODO: review implementation of max height option
export default function Modal({
  open,
  onClose,
  showBackDrop = true,
  children,
  closeButtonClass,
  className,
  options = {},
  preventClose = false,
}: any) {
  const cancelButtonRef = useRef(null);
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const { locale } = router;
  const dir = getDirection(locale);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const openRef = useRef(open);
  openRef.current = open;
  const previousOpenRef = useRef(open);

  const skipPopStateRef = useRef(false);
  const historyEntryAddedRef = useRef(false);

  const { restrictMaxHeight = false } = options;

  // Track only the "open" phase; never run navigation side-effects on unmount.
  useEffect(() => {
    if (!open) return;

    if (preventClose) {
      routerRef.current.beforePopState(() => false);
      return () => {
        routerRef.current.beforePopState(() => true);
      };
    }

    window.history.pushState(
      { [MODAL_HISTORY_STATE_KEY]: true },
      '',
      window.location.href,
    );
    historyEntryAddedRef.current = true;

    const handlePopState = () => {
      if (skipPopStateRef.current) {
        skipPopStateRef.current = false;
        return;
      }
      if (openRef.current) {
        historyEntryAddedRef.current = false;
        onCloseRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [open, preventClose]);

  // Handle programmatic close (open true -> false) deterministically.
  // This is safer than doing history operations in effect cleanup.
  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;

    if (open || !wasOpen) return;
    if (!historyEntryAddedRef.current) return;

    const shouldSkipHistoryCleanup = consumeSkipNextModalHistoryCleanup();
    const st = window.history.state;
    const stillOnModalEntry =
      st &&
      typeof st === 'object' &&
      (st as Record<string, unknown>)[MODAL_HISTORY_STATE_KEY] === true;

    if (!shouldSkipHistoryCleanup && stillOnModalEntry) {
      skipPopStateRef.current = true;
      window.history.back();
    }
    historyEntryAddedRef.current = false;
  }, [open]);

  const handleDialogClose = useCallback(() => {
    if (preventClose) return;
    if (historyEntryAddedRef.current) {
      skipPopStateRef.current = true;
      window.history.back();
      historyEntryAddedRef.current = false;
    }
    onClose();
  }, [preventClose, onClose]);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className={twMerge(
          classNames('fixed inset-0 z-[70] overflow-y-auto', className),
        )}
        initialFocus={cancelButtonRef}
        open={open}
        onClose={preventClose ? () => {} : handleDialogClose}
        dir={dir}
      >
        <div className="min-h-full text-center md:p-5">
          {showBackDrop ? (
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 h-full w-full bg-gray-900 bg-opacity-50" />
            </Transition.Child>
          ) : (
            ''
          )}

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="min-w-content relative inline-block max-w-full align-middle transition-all ltr:text-left rtl:text-right">
              <button type="button"
                onClick={handleDialogClose}
                aria-label="Close panel"
                ref={cancelButtonRef}
                className={twMerge(
                  classNames(
                    'absolute top-4 z-[60] inline-block outline-none focus:outline-0 ltr:right-4 rtl:left-4 lg:hidden',
                    closeButtonClass,
                  ),
                )}
              >
                <span className="sr-only">{'fechar'}</span>
                <CloseIcon />
              </button>
              <div
                className={classNames(
                  'overflow-y-auto min-h-[100vh] md:min-h-full md:overflow-y-visible md:no-scrollbar',
                  { 'max-h-[100vh]': !restrictMaxHeight },
                )}
              >
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
